import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarCheck, Users, Hourglass, Zap, Flame } from 'lucide-react';

/**
 * Phase 313 — Live RSVP Roster widget for the coach dashboard.
 *
 * Pulls /api/coach/sessions/today every 60 seconds (no SSE wiring yet —
 * polling is good enough for a 4-hour-before sweep cadence). Renders one
 * row per session with the three RSVP buckets:
 *
 *      🟢 X Attending   🔴 Y Declined   ⚪ Z Pending
 *
 * Self-hides if (a) the fetch fails or (b) there are zero sessions today
 * (a coach with no sessions today doesn't need a widget cluttering the
 * dashboard).
 */
/**
 * Phase 313.5 — Countdown badge:
 *  • T > 4h  →  ⏳ "AI RSVPs fire in {N} mins"
 *  • T ≤ 4h, RSVPs dispatched, yield_flash_dispatched_at set → 🔥 "Flash Fill Active"
 *  • T ≤ 4h, RSVPs dispatched         → ⚡ "RSVPs Dispatched"
 *  • Anything else (rare race window) → returns null (badge hidden).
 *
 * `session.start_time` is "HH:MM" in IST. We bake today's IST date in
 * so the math is consistent with the backend's IST-anchored sweeper.
 */
function CountdownBadge({ session }) {
  if (!session) return null;
  const startTime = session.start_time;
  if (!startTime || !/^\d{2}:\d{2}/.test(startTime)) return null;

  // Now in IST.
  const nowIst = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
  const [h, m] = startTime.split(':').map(Number);
  const startIst = new Date(Date.UTC(
    nowIst.getUTCFullYear(), nowIst.getUTCMonth(), nowIst.getUTCDate(),
    h, m, 0, 0,
  ));
  const diffMin = Math.round((startIst.getTime() - nowIst.getTime()) / 60000);
  const minutesUntilSweep = diffMin - 240; // 4h before start

  if (session.yield_flash_dispatched) {
    return (
      <Badge
        variant="outline"
        className="mt-1 text-[10px] border-orange-300 text-orange-800 bg-orange-50"
        data-testid={`rsvp-badge-flash-${session.id}`}
      >
        <Flame className="h-3 w-3 mr-1" /> Flash Fill Active
      </Badge>
    );
  }
  if (session.rsvp_dispatched) {
    return (
      <Badge
        variant="outline"
        className="mt-1 text-[10px] border-emerald-300 text-emerald-800 bg-emerald-50"
        data-testid={`rsvp-badge-dispatched-${session.id}`}
      >
        <Zap className="h-3 w-3 mr-1" /> RSVPs Dispatched
      </Badge>
    );
  }
  if (minutesUntilSweep > 0) {
    return (
      <Badge
        variant="outline"
        className="mt-1 text-[10px] border-amber-300 text-amber-800 bg-amber-50"
        data-testid={`rsvp-badge-countdown-${session.id}`}
      >
        <Hourglass className="h-3 w-3 mr-1" /> AI RSVPs fire in {minutesUntilSweep} min
      </Badge>
    );
  }
  if (minutesUntilSweep <= 0 && diffMin > 0) {
    // Sweep window has opened but the cron hasn't run yet (60s tick).
    return (
      <Badge
        variant="outline"
        className="mt-1 text-[10px] border-amber-300 text-amber-800 bg-amber-50"
        data-testid={`rsvp-badge-firing-${session.id}`}
      >
        <Hourglass className="h-3 w-3 mr-1" /> AI sweep firing momentarily
      </Badge>
    );
  }
  return null;
}


function CountPill({ tone, label, value, testId }) {
  const toneMap = {
    green:  'bg-emerald-50 text-emerald-800 ring-emerald-200',
    red:    'bg-rose-50    text-rose-800    ring-rose-200',
    gray:   'bg-slate-50   text-slate-700   ring-slate-200',
  };
  const dot = {
    green:  'bg-emerald-500',
    red:    'bg-rose-500',
    gray:   'bg-slate-400',
  };
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${toneMap[tone] || toneMap.gray}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot[tone] || dot.gray}`} />
      <span className="tabular-nums">{value}</span>
      <span className="text-[10px] uppercase tracking-wide opacity-70">{label}</span>
    </span>
  );
}

export default function LiveRsvpRoster() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchToday = () => {
    api.get('/coach/sessions/today')
      .then((r) => { setData(r.data); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchToday();
    const id = setInterval(fetchToday, 60_000);   // refresh every 60s
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return <Skeleton className="h-32 rounded-2xl" data-testid="rsvp-roster-loading" />;
  }
  if (error || !data) return null;
  if (!Array.isArray(data.sessions) || data.sessions.length === 0) return null;

  const totals = data.totals || { attending: 0, declined: 0, pending: 0 };
  const totalCount = (totals.attending || 0) + (totals.declined || 0) + (totals.pending || 0);

  return (
    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/20 dark:to-transparent" data-testid="live-rsvp-roster">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-sm flex items-center gap-1.5">
              <CalendarCheck className="h-4 w-4 text-emerald-600" />
              Today&apos;s Roster &middot; Live RSVPs
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Auto-refreshes every minute. AI fires WhatsApp reminders 4 hours before each session.
            </p>
          </div>
          {totalCount > 0 && (
            <div className="flex items-center gap-1.5 shrink-0" data-testid="rsvp-totals-strip">
              <CountPill tone="green" label="yes"  value={totals.attending || 0} testId="rsvp-total-attending" />
              <CountPill tone="red"   label="no"   value={totals.declined || 0}  testId="rsvp-total-declined" />
              <CountPill tone="gray"  label="?"    value={totals.pending || 0}   testId="rsvp-total-pending" />
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0 pb-3">
        <ul className="divide-y divide-emerald-100 rounded-lg border border-emerald-100 bg-white dark:bg-slate-900 overflow-hidden">
          {data.sessions.map((s) => {
            const c = s.counts || {};
            const enrolled = (c.attending || 0) + (c.declined || 0) + (c.pending || 0);
            return (
              <li
                key={s.id}
                className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between"
                data-testid={`rsvp-session-row-${s.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{s.title || s.batch || 'Session'}</p>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    {s.start_time && <span>{s.start_time}</span>}
                    {s.duration_minutes ? <span>&middot; {s.duration_minutes}m</span> : null}
                    {s.batch && <span>&middot; {s.batch}</span>}
                    {enrolled > 0 && (
                      <span className="inline-flex items-center gap-1">
                        &middot; <Users className="h-3 w-3" /> {enrolled}
                      </span>
                    )}
                  </div>
                  {/* Phase 313.5 CountdownBadge supersedes the older
                      static "Reminders queued" badge — single source of
                      truth for the session's sweep state. */}
                  <CountdownBadge session={s} />
                </div>
                <div className="flex items-center gap-1.5 shrink-0" data-testid={`rsvp-session-counts-${s.id}`}>
                  <CountPill tone="green" label="yes" value={c.attending || 0} testId={`rsvp-count-attending-${s.id}`} />
                  <CountPill tone="red"   label="no"  value={c.declined || 0}  testId={`rsvp-count-declined-${s.id}`} />
                  <CountPill tone="gray"  label="?"   value={c.pending || 0}   testId={`rsvp-count-pending-${s.id}`} />
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
